import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CTA from '../../../CTA';
import toast from 'react-hot-toast';
import { IoArrowBackOutline as Back } from '@react-icons/all-files/io5/IoArrowBackOutline';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import styles from '../../../Register/StyleRegister/step3.module.scss';

const AuthQuoteModal = ({ onRegister, isLoading, onVerifyCode, onSetPassword }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: registro, 2: verificación código, 3: establecer contraseña
    const [formData, setFormData] = useState({
        ruc: '',
        razonSocial: '',
        telefono: ''
    });
    const [verificationCode, setVerificationCode] = useState({ 0: '', 1: '', 2: '', 3: '' });
    const [disableVerify, setDisableVerify] = useState(true);
    const [passwordData, setPasswordData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [userId, setUserId] = useState(null);

    useEffect(() => {
        validateVerificationCode();
    }, [verificationCode]);

    const validateVerificationCode = () => {
        if (
            verificationCode[0] === '' ||
            verificationCode[1] === '' ||
            verificationCode[2] === '' ||
            verificationCode[3] === ''
        ) {
            return setDisableVerify(true);
        }
        setDisableVerify(false);
    };

    const handleChange = (field) => (e) => {
        setFormData({
            ...formData,
            [field]: e.target.value
        });
    };

    const handlePhoneChange = (value) => {
        setFormData({
            ...formData,
            telefono: value
        });
    };

    const handleRegisterSubmit = () => {
        if (!formData.ruc || !formData.razonSocial || !formData.telefono) {
            toast.error('Por favor complete todos los campos');
            return;
        }
        
        if (formData.ruc.length !== 13) {
            toast.error('El RUC debe tener 13 dígitos');
            return;
        }

        onRegister(formData, (returnedUserId) => {
            
            // Callback que se ejecuta cuando el registro es exitoso
            if (returnedUserId) {
                setUserId(returnedUserId);
                setStep(2);
            } else {
                toast.error('Error: No se recibió el ID de usuario. Por favor intenta de nuevo.');
            }
        });
    };

    const handleVerificationCodeChange = (e) => {
        const key = e.key;
        const { maxLength, value, name, nextSibling, previousSibling } = e.target;

        setVerificationCode((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (value.length >= maxLength) {
            if (nextSibling !== null) {
                nextSibling.focus();
            }
        }

        if (key === 'Backspace') {
            if (previousSibling !== null) {
                setVerificationCode((prev) => ({
                    ...prev,
                    [name]: '',
                }));
                previousSibling.focus();
            } else {
                setVerificationCode((prev) => ({
                    ...prev,
                    [name]: '',
                }));
            }
        }
    };

    const handleVerifySubmit = (e) => {
        e.preventDefault();
        const formattedCode = verificationCode[0] + verificationCode[1] + verificationCode[2] + verificationCode[3];
                
        onVerifyCode(formattedCode, formData.telefono, (returnedUserId) => {            
            if (returnedUserId) {
                setUserId(returnedUserId);
                setStep(3);
            } else if (userId) {
                setStep(3);
            } else {
                toast.error('Error: No se pudo obtener el identificador de usuario. Por favor contacta a soporte.');
            }
        });
    };

    const handlePasswordChange = (field) => (e) => {
        setPasswordData({
            ...passwordData,
            [field]: e.target.value
        });
    };

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
                
        if (!userId) {
            toast.error('Error: No se puede establecer la contraseña sin un ID de usuario válido.');
            return;
        }
        
        if (passwordData.password !== passwordData.confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }
        if (passwordData.password.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }
        
        console.log('✅ Llamando onSetPassword con userId:', userId);
        onSetPassword(userId, passwordData.password);
    };

    const handleGoToLogin = () => {
        navigate('/ingreso');
    };

    const handleGoBack = () => {
        if (step === 2) {
            setStep(1);
        } else if (step === 3) {
            setStep(2);
        }
    };

    if (step === 3) {
        return (
            <Box component="form" onSubmit={handlePasswordSubmit}>
                <Box display="flex" alignItems="center" mb={2}>
                    <Back 
                        style={{ cursor: 'pointer', marginRight: '10px', fontSize: '24px' }} 
                        onClick={handleGoBack}
                    />
                    <Typography fontSize={24} fontWeight={'bold'}>
                        Establecer Contraseña
                    </Typography>
                </Box>
                <Typography fontSize={15} color={'#6d6d6d'} mb={4}>
                    Por favor crea una contraseña segura para tu cuenta.
                </Typography>

                <Box display={'flex'} flexDirection={'column'} gap={3}>
                    <Box>
                        <Typography mb={1.5} fontWeight={500}>Contraseña *</Typography>
                        <TextField
                            fullWidth
                            size='medium'
                            type='password'
                            value={passwordData.password}
                            onChange={handlePasswordChange('password')}
                            placeholder='Mínimo 6 caracteres'
                            inputProps={{ minLength: 6 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                }
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography mb={1.5} fontWeight={500}>Confirmar Contraseña *</Typography>
                        <TextField
                            fullWidth
                            size='medium'
                            type='password'
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange('confirmPassword')}
                            placeholder='Repite tu contraseña'
                            inputProps={{ minLength: 6 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                }
                            }}
                        />
                    </Box>
                </Box>

                <Box mt={4}>
                    <CTA
                        type="submit"
                        isDisabled={!passwordData.password || !passwordData.confirmPassword || isLoading}
                        onClick={handlePasswordSubmit}
                        text={isLoading ? 'Guardando...' : 'Guardar Contraseña'}
                    />
                </Box>
            </Box>
        );
    }

    if (step === 2) {
        return (
            <Box component="form" onSubmit={handleVerifySubmit}>
                <Box display="flex" alignItems="center" mb={2}>
                    <Back 
                        style={{ cursor: 'pointer', marginRight: '10px', fontSize: '24px' }} 
                        onClick={handleGoBack}
                    />
                    <Typography fontSize={24} fontWeight={'bold'}>
                        Verificación
                    </Typography>
                </Box>
                <Typography fontSize={15} color={'#6d6d6d'} mb={4}>
                    Ingresa el código de verificación que hemos enviado a tu teléfono.
                </Typography>

                <Box display="flex" justifyContent="center" gap={1.5} mb={4}>
                    <input
                        type="text"
                        defaultValue={verificationCode[0]}
                        name="0"
                        placeholder="0"
                        className={styles.input1}
                        maxLength={1}
                        onKeyUp={handleVerificationCodeChange}
                        autoFocus
                        style={{
                            width: '55px',
                            height: '55px',
                            textAlign: 'center',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            border: '1px solid #c4c4c4',
                            borderRadius: '8px',
                            outline: 'none',
                        }}
                    />
                    <input
                        type="text"
                        defaultValue={verificationCode[1]}
                        name="1"
                        placeholder="0"
                        className={styles.input2}
                        maxLength={1}
                        onKeyUp={handleVerificationCodeChange}
                        style={{
                            width: '55px',
                            height: '55px',
                            textAlign: 'center',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            border: '1px solid #c4c4c4',
                            borderRadius: '8px',
                            outline: 'none',
                        }}
                    />
                    <input
                        type="text"
                        defaultValue={verificationCode[2]}
                        name="2"
                        placeholder="0"
                        className={styles.input3}
                        maxLength={1}
                        onKeyUp={handleVerificationCodeChange}
                        style={{
                            width: '55px',
                            height: '55px',
                            textAlign: 'center',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            border: '1px solid #c4c4c4',
                            borderRadius: '8px',
                            outline: 'none',
                        }}
                    />
                    <input
                        type="text"
                        defaultValue={verificationCode[3]}
                        name="3"
                        placeholder="0"
                        className={styles.input4}
                        maxLength={1}
                        onKeyUp={handleVerificationCodeChange}
                        style={{
                            width: '55px',
                            height: '55px',
                            textAlign: 'center',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            border: '1px solid #c4c4c4',
                            borderRadius: '8px',
                            outline: 'none',
                        }}
                    />
                </Box>

                <Box mt={4}>
                    <CTA
                        type="submit"
                        isDisabled={disableVerify || isLoading}
                        onClick={handleVerifySubmit}
                        text={isLoading ? 'Verificando...' : 'Confirmar'}
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <Typography fontSize={24} fontWeight={'bold'} mb={2}>
                Regístrate para Cotizar
            </Typography>
            <Typography fontSize={15} color={'#6d6d6d'} mb={4}>
                Para continuar con tu cotización, necesitamos algunos datos
            </Typography>

            <Box display={'flex'} flexDirection={'column'} gap={3}>
                <Box>
                    <Typography mb={1.5} fontWeight={500}>RUC *</Typography>
                    <TextField
                        fullWidth
                        size='medium'
                        type='text'
                        value={formData.ruc}
                        onChange={handleChange('ruc')}
                        placeholder='20123456789'
                        inputProps={{ maxLength: 13 }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            }
                        }}
                    />
                </Box>

                <Box>
                    <Typography mb={1.5} fontWeight={500}>Razón Social *</Typography>
                    <TextField
                        fullWidth
                        size='medium'
                        type='text'
                        value={formData.razonSocial}
                        onChange={handleChange('razonSocial')}
                        placeholder='Nombre de tu empresa'
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            }
                        }}
                    />
                </Box>

                <Box>
                    <Typography mb={1.5} fontWeight={500}>Teléfono *</Typography>
                    <PhoneInput
                        defaultCountry="EC"
                        value={formData.telefono}
                        onChange={handlePhoneChange}
                        placeholder="Teléfono"
                        style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: '1px solid #c4c4c4',
                            borderRadius: '8px',
                            fontSize: '16px'
                        }}
                    />
                </Box>
            </Box>

            <Box mt={4}>
                <CTA
                    isDisabled={isLoading}
                    onClick={handleRegisterSubmit}
                    text={isLoading ? 'Registrando...' : 'Registrarse y Cotizar'}
                />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box display={'flex'} justifyContent={'center'}>
                <Button 
                    onClick={handleGoToLogin}
                    sx={{ textTransform: 'none', fontSize: '15px' }}
                >
                    ¿Ya tienes cuenta? Inicia sesión
                </Button>
            </Box>
        </Box>
    );
};

export default AuthQuoteModal;
